import { create } from 'zustand';
import { platformEvents } from '@quickbee/events';

// src/branch/branchScopes.ts
var UNPINNED_SCOPES = /* @__PURE__ */ new Set(["own", "partner", "tenant", "any"]);
var ALL = { all: true, codes: [] };
function unrestrictedGrant() {
  return ALL;
}
function parseOrgScope(claim) {
  if (!claim || !claim.trim()) return {};
  const scopes = {};
  for (const entry of claim.split(";")) {
    if (!entry.trim()) continue;
    const split = entry.indexOf("=");
    if (split <= 0) continue;
    const role = entry.slice(0, split).trim().toUpperCase();
    if (!role) continue;
    scopes[role] = parseValue(entry.slice(split + 1).trim());
  }
  return scopes;
}
function parseValue(value) {
  const pin = value.indexOf("|");
  if (pin >= 0) {
    return value.slice(0, pin).trim() === "branch" ? pinnedTo(value.slice(pin + 1)) : { all: false, codes: [] };
  }
  if (UNPINNED_SCOPES.has(value)) return ALL;
  if (value === "branch") return { all: false, codes: [] };
  if (value === "*") return ALL;
  return pinnedTo(value);
}
function pinnedTo(list) {
  return {
    all: false,
    codes: [
      ...new Set(
        list.split(",").map((code) => code.trim().toUpperCase()).filter(Boolean)
      )
    ]
  };
}
function formatOrgScope(scopes) {
  return Object.entries(scopes).map(([role, grant]) => `${role}=${grant.all ? "tenant" : `branch|${grant.codes.join(",")}`}`).join(";");
}
function grantForRole(scopes, role) {
  return scopes?.[role.trim().toUpperCase()] ?? ALL;
}
function isBranchPinned(scopes, role) {
  return Boolean(scopes && role.trim().toUpperCase() in scopes);
}
function reachableBranches(scopes) {
  if (!scopes) return null;
  const roles = Object.values(scopes);
  if (roles.length === 0) return null;
  const union = /* @__PURE__ */ new Set();
  for (const grant of roles) {
    if (grant.all) return null;
    for (const code of grant.codes) union.add(code);
  }
  return [...union];
}
function coversBranch(scopes, branchCode) {
  if (!branchCode) return false;
  const reachable = reachableBranches(scopes);
  return reachable === null || reachable.includes(branchCode.trim().toUpperCase());
}
function soleBranch(scopes) {
  const reachable = reachableBranches(scopes);
  return reachable !== null && reachable.length === 1 ? reachable[0] : null;
}
var initialState = {
  user: null,
  tenantConfig: null,
  isAuthenticated: false,
  isLoading: true,
  kycTier: null,
  roles: [],
  accessToken: null,
  delegation: null,
  activeBranch: null
};
function seedActiveBranch(user, current) {
  if (current && coversBranch(user.branchScopes, current)) return current;
  return soleBranch(user.branchScopes);
}
var useAuthStore = create((set) => ({
  ...initialState,
  setUser: (user) => set((state) => ({
    user,
    isAuthenticated: true,
    isLoading: false,
    kycTier: user.kycTier,
    roles: user.roles,
    activeBranch: seedActiveBranch(user, state.activeBranch)
  })),
  setSession: ({ user, accessToken }) => set((state) => ({
    user,
    accessToken,
    isAuthenticated: true,
    isLoading: false,
    kycTier: user.kycTier,
    roles: user.roles,
    activeBranch: seedActiveBranch(user, state.activeBranch)
  })),
  setAccessToken: (accessToken) => set({ accessToken }),
  beginDelegation: (delegation) => set({ delegation }),
  endDelegation: () => set({ delegation: null }),
  setActiveBranch: (branchCode) => set((state) => {
    if (branchCode === null) return { activeBranch: null };
    const normalised = branchCode.trim().toUpperCase();
    return coversBranch(state.user?.branchScopes, normalised) ? { activeBranch: normalised } : {};
  }),
  // `tenantConfig` deliberately survives: the tenant is resolved from the HOST
  // (initTenant / the shell's tenant-resolver), not from the session, so it is
  // just as valid signed-out as signed-in. Wiping it here made every settle-to-
  // unauthenticated bootstrap forget the white-label branding AND the tenant id
  // — and the next login call then went out without X-Tenant-ID, which the
  // backend rejects (MISSING_TENANT_400_001) before any handler runs.
  // A delegation never survives a logout or an expired session: `initialState` carries
  // `delegation: null`, so tearing the session down also stops the operator acting as
  // anybody else. That is the correct direction — the delegation is spent WITH the
  // operator's identity, so losing the identity must lose the delegation too.
  clearAuth: () => set((state) => ({ ...initialState, tenantConfig: state.tenantConfig, isLoading: false })),
  setLoading: (loading) => set({ isLoading: loading }),
  setTenantConfig: (tenantConfig) => set({ tenantConfig })
}));
var authEvents = {
  on: (event, handler) => platformEvents.on(event, handler),
  off: (event, handler) => platformEvents.off(event, handler),
  emit: (event, payload) => platformEvents.emit(event, payload)
};

export { authEvents, coversBranch, formatOrgScope, grantForRole, isBranchPinned, parseOrgScope, reachableBranches, soleBranch, unrestrictedGrant, useAuthStore };
//# sourceMappingURL=chunk-G2RM2HWO.js.map
//# sourceMappingURL=chunk-G2RM2HWO.js.map